import { motion } from "motion/react";
import { Download, Award, BookOpen, Coffee, Heart } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { skills, tools } from "../data/portfolio";

export default function About() {
  const experience = [
    {
      company: 'TechCorp',
      role: 'Senior Product Designer',
      period: '2022 - Present',
      description: 'Leading design for AI-powered productivity tools, managing a team of 3 designers, and establishing design systems across 5 product lines.'
    },
    {
      company: 'FinanceFlow',
      role: 'Product Designer',
      period: '2020 - 2022',
      description: 'Redesigned mobile banking experience for 2M+ users, conducted user research, and collaborated with cross-functional teams to ship 15+ features.'
    },
    {
      company: 'StartupXYZ',
      role: 'UX Designer',
      period: '2018 - 2020',
      description: 'First design hire at early-stage startup. Built design process from scratch, designed 0-1 products, and worked directly with founders on product strategy.'
    },
    {
      company: 'Design Agency Co',
      role: 'Junior Designer',
      period: '2016 - 2018',
      description: 'Worked on client projects across various industries, learned fundamental design skills, and contributed to brand identity and web design projects.'
    }
  ];

  const awards = [
    {
      title: 'UX Design Awards - Best Mobile Experience',
      year: '2024',
      project: 'FinTech Mobile Banking App'
    },
    {
      title: 'Good Design Award - Digital Innovation',
      year: '2023',
      project: 'AI Productivity Assistant'
    },
    {
      title: 'Webby Awards - Best User Experience',
      year: '2022',
      project: 'Enterprise Dashboard Design'
    }
  ];

  const interests = [
    { icon: BookOpen, label: 'Reading design books & articles' },
    { icon: Coffee, label: 'Coffee brewing & cafe hopping' },
    { icon: Heart, label: 'Volunteering for accessibility advocacy' },
    { icon: Award, label: 'Mentoring junior designers' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 md:pt-40 pb-20 px-4"
    >
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-16 items-center mb-20"
        >
          <div>
            <h1 className="text-4xl md:text-5xl text-foreground mb-6 leading-tight">
              Hi, I'm Jane Doe
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              I'm a Senior Product Designer with 8+ years of experience creating 
              user-centered digital experiences. I'm passionate about solving complex 
              problems through research, empathy, and thoughtful design.
            </p>
            
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Currently based in San Francisco, I work with teams to bridge the gap 
              between business goals and user needs. My approach combines rigorous 
              research with creative problem-solving to deliver solutions that are 
              both delightful and functional.
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button className="inline-flex items-center space-x-2 bg-foreground text-background px-6 py-3 rounded-xl hover:bg-foreground/90 transition-colors">
                <Download className="w-4 h-4" />
                <span>Download Resume</span>
              </button>
              
              <a
                href="mailto:jane@example.com"
                className="inline-flex items-center justify-center space-x-2 border border-border px-6 py-3 rounded-xl hover:bg-accent transition-colors"
              >
                <span>Get in touch</span>
              </a>
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-[4/5] bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl overflow-hidden">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1671726805768-575f88de945a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9kdWN0JTIwZGVzaWduJTIwcG9ydGZvbGlvJTIwd29ya3NwYWNlfGVufDF8fHx8MTc1NzYwNTQxN3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Jane Doe"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Floating quote */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -bottom-4 -left-4 bg-white backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-lg max-w-xs"
            >
              <p className="text-sm text-muted-foreground italic">
                "Great design is not just about how it looks, but how it works for real people."
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Experience */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl text-foreground mb-12">Experience</h2>
          
          <div className="grid gap-6 relative">
            {/* Connecting line */}
            <div className="absolute left-8 top-12 bottom-0 w-px bg-gradient-to-b from-foreground/20 via-foreground/10 to-transparent hidden md:block" />
            
            {experience.map((job, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ scale: 1.02, y: -4 }}
                className="group relative"
              >
                {/* Timeline dot */}
                <div className="absolute left-8 top-8 w-4 h-4 bg-foreground rounded-full border-4 border-background shadow-lg z-10 hidden md:block group-hover:scale-125 transition-transform duration-300" />
                
                {/* Experience card */}
                <div className="bg-card/60 glass border border-border/50 rounded-2xl p-6 md:ml-20 transition-all duration-300 group-hover:bg-card/80 group-hover:border-foreground/20 group-hover:shadow-lg">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl text-foreground group-hover:text-foreground transition-colors duration-300">
                        {job.role}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <h4 className="text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          {job.company}
                        </h4>
                        <div className="w-1 h-1 bg-muted-foreground rounded-full" />
                        <span className="text-muted-foreground text-sm group-hover:text-foreground/70 transition-colors duration-300">
                          {job.period}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                    {job.description}
                  </p>
                  
                  {/* Decorative elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-foreground/5 to-foreground/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 right-6 w-4 h-4 bg-gradient-to-br from-foreground/10 to-foreground/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100" />
                </div>
              </motion.div>
            ))}
            
            {/* Bottom fade effect */}
            <div className="absolute bottom-0 left-8 w-px h-12 bg-gradient-to-b from-foreground/10 to-transparent hidden md:block" />
          </div>
        </motion.section>

        {/* Skills & Tools */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Skills Section */}
            <div>
              <h3 className="text-2xl text-foreground mb-8">Skills</h3>
              <div className="space-y-6">
                {[
                  { name: 'User Research', percentage: 95 },
                  { name: 'Prototyping', percentage: 90 },
                  { name: 'Visual Design', percentage: 88 },
                  { name: 'Design Systems', percentage: 92 },
                  { name: 'Usability Testing', percentage: 85 },
                  { name: 'Figma', percentage: 95 },
                  { name: 'Adobe Creative Suite', percentage: 80 },
                  { name: 'HTML/CSS', percentage: 75 }
                ].map((skill, index) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">{skill.name}</span>
                      <span className="text-sm text-muted-foreground">{skill.percentage}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.percentage}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                        className="bg-foreground h-2 rounded-full"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Tools & Software Section */}
            <div>
              <h3 className="text-2xl text-foreground mb-8">Tools & Software</h3>
              <div className="grid grid-cols-3 gap-3 mb-12">
                {[
                  'Figma', 'Sketch', 'Adobe XD', 'Photoshop', 'Illustrator',
                  'InVision', 'Framer', 'Principle', 'Maze', 'Hotjar', 'Miro'
                ].map((tool, index) => (
                  <motion.div
                    key={tool}
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-muted/60 px-3 py-2 rounded-lg text-center"
                  >
                    <span className="text-xs text-foreground">{tool}</span>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.6 }}
                className="bg-muted/60 px-3 py-2 rounded-lg text-center w-fit"
              >
                <span className="text-xs text-foreground">Notion</span>
              </motion.div>
              
              {/* Achievements Section */}
              <div className="mt-12">
                <h3 className="text-2xl text-foreground mb-8">Achievements</h3>
                <div className="space-y-4">
                  {[
                    { icon: Award, text: 'UX Design Awards 2023 Winner' },
                    { icon: BookOpen, text: 'Published 12 research papers' },
                    { icon: Coffee, text: 'Mentored 15+ junior designers' },
                    { icon: Heart, text: 'Speaker at 8 design conferences' }
                  ].map((achievement, index) => (
                    <motion.div
                      key={achievement.text}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <achievement.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{achievement.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Awards */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl text-foreground mb-12">Recognition</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {awards.map((award, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-2xl"
              >
                <Award className="w-8 h-8 text-purple-600 mb-4" />
                <h4 className="font-medium mb-2">{award.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{award.project}</p>
                <span className="text-xs text-purple-600 font-medium">{award.year}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Personal Interests */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl text-foreground mb-12">When I'm Not Designing</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {interests.map((interest, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl hover:bg-muted/30 transition-colors"
              >
                <interest.icon className="w-8 h-8 text-purple-600 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">{interest.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Philosophy */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl text-foreground mb-6">My Design Philosophy</h2>
          
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Design is not just about making things look beautiful—it's about creating 
            meaningful experiences that solve real problems for real people. I believe 
            in the power of empathy, research, and iteration to create solutions that 
            truly make a difference.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: 'Empathy First',
                description: 'Understanding users deeply through research and observation'
              },
              {
                title: 'Inclusive Design',
                description: 'Creating experiences that work for everyone, regardless of ability'
              },
              {
                title: 'Continuous Learning',
                description: 'Always questioning, testing, and improving based on feedback'
              }
            ].map((principle, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <h4 className="font-medium mb-2">{principle.title}</h4>
                <p className="text-sm text-muted-foreground">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
}
